import React, { useState, useEffect, useMemo } from 'react'
import { Theme, Dialog } from '@material-ui/core'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import ToggleButton from '@material-ui/lab/ToggleButton'
import { Close } from '@material-ui/icons'
import axios from 'axios'

import {
    ResidentTermDirectoryRow,
    DeliverableValue,
    DeliverableValueStatus,
    GroupedValue,
    GroupedValuesByTerm,
    ResidentTermDirectoryStatus,
    MergeRequest,
} from '../../../../models'
import {
    StatusIcon,
    SummarySection,
    GroupedDataTable,
    SubmitButtonArea,
    SummaryData,
    AcceptDialog,
} from './components'
import { axiosInstance } from '../../../../helpers'
import { AuditMode } from './modes/AuditMode'
import { MergeMode } from './modes/MergeMode'

// Define lease period types
type LeasePeriodType = 'Current' | 'Future' | 'Past'

// Define panel modes
type PanelMode = 'audit' | 'merge'

export interface ColumnInfo {
    key: number
    label: string
}

export interface ColumnDetailPanelProps {
    open: boolean
    onClose: () => void
    columnLabel: string
    columnKey?: number
    tableRect: { top: number; left: number; width: number; height: number }
    theme: Theme
    rows?: ResidentTermDirectoryRow[]
    onMarkAsGood?: (valueId: number) => void
    onMarkMultipleAsGood?: (valueIds: number[]) => void
    availableColumns?: ColumnInfo[]
    onColumnChange?: (columnKey: number) => void
    onViewDetails?: (
        entityValue: DeliverableValue,
        row: ResidentTermDirectoryRow,
    ) => void
    leaseAuditId: number
    bulkAcceptDeliverableValues?: (
        deliverableValueIds: number[],
        notes: string,
    ) => Promise<void>
    mergeResidentTermDirectories: (params: MergeRequest) => Promise<void>
    isMerging: boolean
}

// Helper function to determine lease period type
const getLeasePeriodType = (
    startDate: string,
    endDate: string,
): LeasePeriodType => {
    const now = new Date()
    const leaseStart = new Date(startDate)
    const leaseEnd = new Date(endDate)

    if (leaseEnd < now) {
        return 'Past'
    } else if (leaseStart > now) {
        return 'Future'
    } else {
        return 'Current'
    }
}

export const ColumnDetailPanel: React.FC<ColumnDetailPanelProps> = ({
    open,
    onClose,
    columnLabel,
    columnKey,
    tableRect,
    theme,
    rows = [],
    onMarkAsGood,
    onMarkMultipleAsGood,
    availableColumns = [],
    onColumnChange,
    onViewDetails,
    leaseAuditId,
    bulkAcceptDeliverableValues,
    mergeResidentTermDirectories,
    isMerging,
}) => {
    useEffect(() => {
        if (open) {
            const handleScroll = () => {
                // If the panel is open, we want to close it when the user scrolls
                // This prevents the panel from becoming misaligned with the table
                onClose()
            }

            window.addEventListener('scroll', handleScroll)

            return () => {
                window.removeEventListener('scroll', handleScroll)
            }
        }
    }, [open, onClose])

    // State for filtering
    const [statusFilter, setStatusFilter] = useState<
        DeliverableValueStatus | 'ALL'
    >('ALL')

    // Add loading state for large datasets
    const [isProcessing, setIsProcessing] = useState(false)

    // Add state to track selected rows
    const [selectedValueIds, setSelectedValueIds] = useState<number[]>([])

    // Reset selected values when panel closes or column changes
    useEffect(() => {
        if (!open || !columnKey) {
            setSelectedValueIds([])
        }
    }, [open, columnKey])

    // Add state for accept dialog
    const [acceptDialogOpen, setAcceptDialogOpen] = useState(false)

    // Add mode state
    const [mode, setMode] = useState<PanelMode>('audit')

    // Add selected rows for merge mode
    const [
        selectedExcelRow,
        setSelectedExcelRow,
    ] = useState<ResidentTermDirectoryRow | null>(null)
    const [
        selectedPdfRow,
        setSelectedPdfRow,
    ] = useState<ResidentTermDirectoryRow | null>(null)

    // Group the values by lease period (current, future, past) - optimized for performance
    const groupedValues = useMemo<GroupedValuesByTerm[]>(() => {
        if (!columnKey || rows.length === 0) {
            return []
        }

        // Set processing state for large datasets
        if (rows.length > 500) {
            setIsProcessing(true)
        }

        // Find values for the selected column
        const valuesByPeriod: Record<string, GroupedValuesByTerm> = {
            Current: {
                termKey: 'Current',
                leaseStart: '',
                leaseEnd: '',
                values: [],
            },
            Future: {
                termKey: 'Future',
                leaseStart: '',
                leaseEnd: '',
                values: [],
            },
            Past: {
                termKey: 'Past',
                leaseStart: '',
                leaseEnd: '',
                values: [],
            },
        }

        // Use a more efficient loop for large datasets
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i]
            // Find the value for this column using find since we need just one value
            const value = row.values.find(
                (v) => v.entity_classification.id === columnKey,
            )

            if (value) {
                // Determine the period type
                const periodType = getLeasePeriodType(
                    row.lease_start,
                    row.lease_end,
                )

                valuesByPeriod[periodType].values.push({
                    rowId: row.id,
                    customerName: row.customer.name,
                    bldgUnit: row.bldg_unit,
                    leaseStart: row.lease_start,
                    leaseEnd: row.lease_end,
                    value: {
                        id: value.id,
                        status: value.status,
                        document_value: value.document_value,
                        rent_roll_value: value.rent_roll_value,
                        data_type: value.entity_classification.data_type,
                        channel: value.channel,
                        message_count: value.message_count,
                    },
                })
            }
        }

        // Filter out empty period groups and sort them in a logical order
        const periodOrder: LeasePeriodType[] = ['Current', 'Future', 'Past']
        const result = periodOrder
            .map((period) => valuesByPeriod[period])
            .filter((group) => group.values.length > 0)

        // Sort items within each group by lease start date
        result.forEach((group) => {
            group.values.sort(
                (a: GroupedValue, b: GroupedValue) =>
                    new Date(a.leaseStart).getTime() -
                    new Date(b.leaseStart).getTime(),
            )
        })

        // Clear processing state
        setIsProcessing(false)

        return result
    }, [columnKey, rows])

    // Filter the grouped values based on status filter - optimized for large datasets
    const filteredGroupedValues = useMemo(() => {
        // No filtering needed for ALL
        if (statusFilter === 'ALL') {
            return groupedValues
        }

        // Set processing state for filtering large datasets
        if (groupedValues.length > 50) {
            setIsProcessing(true)
        }

        // More efficient filtering approach for large datasets
        const result = groupedValues
            .map((group) => {
                // Create shallow copy with filtered values array
                return {
                    ...group,
                    values: group.values.filter(
                        (item: GroupedValue) =>
                            item.value.status === statusFilter,
                    ),
                }
            })
            .filter((group) => group.values.length > 0)

        // Clear processing state after filtering
        setIsProcessing(false)

        return result
    }, [groupedValues, statusFilter])

    // Calculate summary statistics
    const summary = useMemo<SummaryData>(() => {
        if (!columnKey || rows.length === 0) {
            return {
                total: 0,
                matched: 0,
                notMatched: 0,
                missingRentRoll: 0,
                missingDocument: 0,
                matchFixed: 0,
                accepted: 0,
            }
        }

        let total = 0
        let matched = 0
        let notMatched = 0
        let missingRentRoll = 0
        let missingDocument = 0
        let matchFixed = 0
        let accepted = 0

        groupedValues.forEach((group) => {
            group.values.forEach((item: GroupedValue) => {
                total++
                switch (item.value.status) {
                    case 'MATCHED':
                        matched++
                        break
                    case 'NOT_MATCHED':
                        notMatched++
                        break
                    case 'MISSING_RENT_ROLL':
                        missingRentRoll++
                        break
                    case 'MISSING_DOCUMENT':
                        missingDocument++
                        break
                    case 'MATCH_FIXED':
                        matchFixed++
                        break
                    case 'ACCEPTED':
                        accepted++
                        break
                }
            })
        })

        return {
            total,
            matched,
            notMatched,
            missingRentRoll,
            missingDocument,
            matchFixed,
            accepted,
        }
    }, [columnKey, rows, groupedValues])

    // Handle checkbox toggle
    const handleCheckboxToggle = (valueId: number) => {
        setSelectedValueIds((prev) => {
            const isSelected = prev.includes(valueId)
            if (isSelected) {
                return prev.filter((id) => id !== valueId)
            } else {
                return [...prev, valueId]
            }
        })
    }

    // Function to make the API call for bulk accept
    const handleBulkAccept = async (notes: string) => {
        if (selectedValueIds.length === 0) return

        try {
            setIsProcessing(true)

            // Use the prop function instead of making a direct API call
            if (bulkAcceptDeliverableValues) {
                await bulkAcceptDeliverableValues(selectedValueIds, notes)
            }

            // If successful, close the dialog and the panel
            setAcceptDialogOpen(false)
            onClose()

            // You would typically show a success notification here
            console.log(
                `Successfully accepted ${selectedValueIds.length} items`,
            )
        } catch (error) {
            console.error('Error accepting items:', error)
            // You would typically show an error notification here
        } finally {
            setIsProcessing(false)
        }
    }

    // Handle opening the accept dialog
    const handleOpenAcceptDialog = () => {
        if (selectedValueIds.length > 0) {
            setAcceptDialogOpen(true)
        }
    }

    // Handle closing the accept dialog
    const handleCloseAcceptDialog = () => {
        setAcceptDialogOpen(false)
    }

    // Handle submit button click (opens the dialog)
    const handleSubmit = () => {
        handleOpenAcceptDialog()
    }

    // Handle the header checkbox of a group
    const handleHeaderCheckboxChange = (groupValues: GroupedValue[]) => {
        // Get all eligible value IDs in the group
        const eligibleValueIds = groupValues
            .filter(
                (item) =>
                    item.value.status === 'NOT_MATCHED' ||
                    item.value.status === 'MISSING_RENT_ROLL' ||
                    item.value.status === 'MISSING_DOCUMENT',
            )
            .map((item) => item.value.id)

        // Skip if there are no eligible values
        if (eligibleValueIds.length === 0) return

        setSelectedValueIds((prev) => {
            // Check if all eligible values in the group are already selected
            const allSelected = eligibleValueIds.every((id) =>
                prev.includes(id),
            )

            if (allSelected) {
                // If all are selected, deselect them
                return prev.filter((id) => !eligibleValueIds.includes(id))
            } else {
                // Otherwise, select all eligible values
                const newSelected = [...prev]
                eligibleValueIds.forEach((id) => {
                    if (!newSelected.includes(id)) {
                        newSelected.push(id)
                    }
                })
                return newSelected
            }
        })
    }

    // Check the state of a group's header checkbox
    const getGroupCheckboxState = (groupValues: GroupedValue[]) => {
        // Get all eligible value IDs in the group
        const eligibleValueIds = groupValues
            .filter(
                (item) =>
                    item.value.status === 'NOT_MATCHED' ||
                    item.value.status === 'MISSING_RENT_ROLL' ||
                    item.value.status === 'MISSING_DOCUMENT',
            )
            .map((item) => item.value.id)

        // If no eligible values, return disabled
        if (eligibleValueIds.length === 0) {
            return { checked: false, indeterminate: false, disabled: true }
        }

        // Count how many are selected
        const selectedCount = eligibleValueIds.filter((id) =>
            selectedValueIds.includes(id),
        ).length

        // Return appropriate state
        return {
            checked:
                selectedCount === eligibleValueIds.length &&
                eligibleValueIds.length > 0,
            indeterminate:
                selectedCount > 0 && selectedCount < eligibleValueIds.length,
            disabled: false,
        }
    }

    // Find current column index in available columns
    const currentColumnIndex = useMemo(() => {
        if (availableColumns.length === 0) return -1

        // If no columnKey provided but we have a columnLabel, try to find by label
        if (columnKey === undefined) {
            const indexByLabel = availableColumns.findIndex(
                (col) => col.label === columnLabel,
            )
            if (indexByLabel >= 0) return indexByLabel
        } else {
            // Find by key
            const indexByKey = availableColumns.findIndex(
                (col) => col.key === columnKey,
            )
            if (indexByKey >= 0) return indexByKey
        }

        // If we still don't have a match, default to the first item if available
        return availableColumns.length > 0 ? 0 : -1
    }, [columnKey, columnLabel, availableColumns])

    // Check if we have previous or next columns
    const hasPreviousColumn = currentColumnIndex > 0
    const hasNextColumn = currentColumnIndex < availableColumns.length - 1

    // Handle column navigation
    const handlePreviousColumn = () => {
        if (!hasPreviousColumn || !onColumnChange) return
        const prevColumn = availableColumns[currentColumnIndex - 1]
        onColumnChange(prevColumn.key)
    }

    const handleNextColumn = () => {
        if (!hasNextColumn || !onColumnChange) return
        const nextColumn = availableColumns[currentColumnIndex + 1]
        onColumnChange(nextColumn.key)
    }

    // Check if navigation functionality is fully available
    const isNavigationEnabled =
        availableColumns.length > 1 && typeof onColumnChange === 'function'

    if (
        process.env.NODE_ENV !== 'production' &&
        !isNavigationEnabled &&
        availableColumns.length > 0
    ) {
        // Display a console warning if navigation won't work
        console.warn(
            'Column navigation partially configured: ' +
                `availableColumns: ${availableColumns.length} items, ` +
                `onColumnChange: ${typeof onColumnChange}. ` +
                'Both are required for navigation to work.',
        )
    }

    // Filter rows for merge mode
    const { excelOnlyRows, pdfOnlyRows } = useMemo(() => {
        if (!rows) return { excelOnlyRows: [], pdfOnlyRows: [] }

        return {
            excelOnlyRows: rows.filter((row) => row.status === 'MISSING_LEASE'),
            pdfOnlyRows: rows.filter(
                (row) => row.status === 'MISSING_RENT_ROLL',
            ),
        }
    }, [rows])

    // Handle merge row selection
    const handleRowSelect = (
        row: ResidentTermDirectoryRow | null,
        type: 'excel' | 'pdf',
    ) => {
        if (type === 'excel') {
            setSelectedExcelRow(row)
            // Always clear the other selection when selecting a new row
            setSelectedPdfRow(null)
        } else {
            setSelectedPdfRow(row)
            // Always clear the other selection when selecting a new row
            setSelectedExcelRow(null)
        }
    }

    // Handle merge action
    const handleMerge = async () => {
        if (
            !selectedExcelRow ||
            !selectedPdfRow ||
            !mergeResidentTermDirectories
        )
            return

        try {
            await mergeResidentTermDirectories({
                lease_audit_id: leaseAuditId,
                merges: [
                    {
                        excel_row_id: selectedExcelRow.id,
                        pdf_row_id: selectedPdfRow.id,
                    },
                ],
            })
            setSelectedExcelRow(null)
            setSelectedPdfRow(null)
            onClose()
        } catch (error) {
            console.error('Error merging rows:', error)
        }
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: tableRect.top,
                left: tableRect.left,
                width: tableRect.width,
                height: tableRect.height,
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[4],
                zIndex: theme.zIndex.mobileStepper,
                display: 'flex',
                flexDirection: 'column',
                transform: open
                    ? 'translateY(0)'
                    : 'translateY(calc(100% + 120px))',
                transition: 'transform 0.5s',
                overflow: 'auto',
                borderRadius: '4px',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflowY: 'hidden',
                }}
            >
                {/* Mode Toggle */}
                <div
                    style={{
                        padding: '8px 16px',
                        borderBottom: '1px solid #e0e0e0',
                    }}
                >
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={(
                            event: React.MouseEvent<HTMLElement>,
                            newMode: PanelMode | null,
                        ) => {
                            if (newMode !== null) {
                                setMode(newMode)
                                setSelectedValueIds([])
                                setSelectedExcelRow(null)
                                setSelectedPdfRow(null)
                            }
                        }}
                    >
                        <ToggleButton value="audit">Audit Mode</ToggleButton>
                        <ToggleButton value="merge">Merge Mode</ToggleButton>
                    </ToggleButtonGroup>
                </div>

                {/* Title moved to SummarySection */}
                <SummarySection
                    theme={theme}
                    summary={summary}
                    totalRows={rows.length}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    columnLabel={columnLabel}
                    onClose={onClose}
                    onPreviousColumn={handlePreviousColumn}
                    onNextColumn={handleNextColumn}
                    hasPreviousColumn={hasPreviousColumn && isNavigationEnabled}
                    hasNextColumn={hasNextColumn && isNavigationEnabled}
                />

                <div
                    style={{ padding: '16px', overflowY: 'auto', flexGrow: 1 }}
                >
                    {mode === 'audit' ? (
                        <AuditMode
                            theme={theme}
                            groupedValues={filteredGroupedValues}
                            selectedValueIds={selectedValueIds}
                            onCheckboxToggle={handleCheckboxToggle}
                            onHeaderCheckboxChange={handleHeaderCheckboxChange}
                            getGroupCheckboxState={getGroupCheckboxState}
                            isProcessing={isProcessing}
                            onViewDetails={onViewDetails}
                        />
                    ) : (
                        <MergeMode
                            theme={theme}
                            excelOnlyRows={excelOnlyRows}
                            pdfOnlyRows={pdfOnlyRows}
                            selectedExcelRow={selectedExcelRow}
                            selectedPdfRow={selectedPdfRow}
                            onRowSelect={handleRowSelect}
                            onViewDetails={(row) => {
                                const value = row.values[0]
                                if (value && onViewDetails) {
                                    onViewDetails(value, row)
                                }
                            }}
                            columnKey={columnKey || 0}
                            mergeResidentTermDirectories={
                                mergeResidentTermDirectories
                            }
                            isMerging={isMerging}
                            leaseAuditId={leaseAuditId}
                        />
                    )}
                </div>

                {/* Submit button area */}
                {mode === 'audit' &&
                    filteredGroupedValues.length > 0 &&
                    !isProcessing && (
                        <SubmitButtonArea
                            theme={theme}
                            selectedValueIds={selectedValueIds}
                            onSubmit={handleSubmit}
                        />
                    )}

                {/* Accept Dialog */}
                <AcceptDialog
                    open={acceptDialogOpen}
                    onClose={handleCloseAcceptDialog}
                    onAccept={handleBulkAccept}
                    selectedCount={selectedValueIds.length}
                />
            </div>
        </div>
    )
}
